import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BackendUpdateResponse,
  Bid,
  DatastoreDocument,
  Milestone,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextAlign, TextSize } from '@freelancer/ui/text';
import { requiredTruthy } from '@freelancer/ui/validators';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';

type ReleaseMilestoneRedirectSchema =
  | {
      type: 'COMPLETE';
      bidderId: number;
    }
  | {
      type: 'RELEASE';
    };

@Component({
  template: `
    <fl-bit
      class="MilestoneReleaseModalBody"
      flTrackingSection="MilestoneReleaseModal"
    >
      <fl-picture
        alt="Milestone release warning"
        i18n-alt="Milestone release warning image"
        [alignCenter]="true"
        [src]="'project-view/milestone-release-warning.svg'"
      ></fl-picture>
      <fl-heading
        i18n="Confirm release milestone warning heading"
        [size]="TextSize.MID"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XSMALL"
      >
        Confirm Release of Funds
      </fl-heading>
      <fl-text
        class="Description"
        [flMarginBottom]="Margin.SMALL"
        i18n="Release milestone warning text"
        [fontType]="FontType.SPAN"
        [textAlign]="TextAlign.CENTER"
      >
        Only release milestone payments when you are 100% satisfied with your
        freelancer's work. They cannot be returned once released.
      </fl-text>
      <fl-checkbox
        flTrackingLabel="MilestoneReleaseModalCheckbox"
        [control]="checkControl"
        label="My freelancer has delivered satisfactory work."
        i18n-label="Release milestone checkbox text"
        [flMarginBottom]="Margin.LARGE"
      ></fl-checkbox>

      <fl-hr [flMarginBottom]="Margin.MID"></fl-hr>

      <fl-bit
        *ngIf="(submissionPromise | async)?.status === 'error'"
        [ngSwitch]="(submissionPromise | async)?.errorCode"
        [flMarginBottom]="Margin.SMALL"
      >
        <fl-text
          *ngSwitchCase="'PROFILE_INCOMPLETE'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates incomplete profile"
        >
          You need to
          <fl-link
            flTrackingLabel="MilestoneReleaseModalCompleteProfileLink"
            [link]="'/support/Profile/how-to-edit-your-profile'"
            [newTab]="true"
            >complete your profile</fl-link
          >
          before releasing milestones.
        </fl-text>
        <fl-text
          *ngSwitchCase="'PROJECT_NOT_ACCEPTED'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates project not accepted"
        >
          Your freelancer must accept the project before a Milestone Payment can
          be released. Please remind your freelancer to accept the project
          through messaging.
        </fl-text>
        <fl-text
          *ngSwitchCase="'MILESTONE_DISPUTE_PENDING'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates milestone is under dispute"
        >
          This milestone cannot be released as it is currently under dispute.
        </fl-text>
        <fl-text
          *ngSwitchCase="'NOT_FOUND'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates milestone not found"
        >
          There was an error while trying to retrieve this milestone. Please try
          again or contact support@freelancer.com
          <ng-container *ngIf="(submissionPromise | async)?.requestId">
            with request_id: {{ (submissionPromise | async)?.requestId }}.
          </ng-container>
        </fl-text>
        <fl-text
          *ngSwitchCase="'EMAIL_VERIFICATION_REQUIRED'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates unverified email"
        >
          You need to
          <fl-link
            flTrackingLabel="MilestoneReleaseModalVerifyEmailLink"
            [fragment]="'TrustSettings'"
            [link]="'/users/settings.php'"
            [newTab]="true"
            >verify your email address</fl-link
          >
          before releasing milestones.
        </fl-text>
        <fl-text
          *ngSwitchCase="'PHONE_VERIFICATION_REQUIRED'"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates unverified phone"
        >
          You need to
          <fl-link
            flTrackingLabel="MilestoneReleaseModalVerifyPhoneLink"
            [fragment]="'TrustSettings'"
            [link]="'/users/settings.php'"
            [newTab]="true"
            >verify your phone number</fl-link
          >
          before releasing milestones.
        </fl-text>
        <fl-text
          *ngSwitchCase="ErrorCodeApi.IDENTITY_VERIFICATION_REQUIRED"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="Releasing milestone error that indicates unverified identity"
        >
          Identity verification required for account security reasons. You can
          continue to work and receive payments, but you won't be able to
          transfer or withdraw funds. To verify your identity, visit the
          <fl-link
            flTrackingLabel="VerificationCenterLink"
            [link]="'/users/kyc/verificationCenter.php'"
            [newTab]="true"
          >
            Verification Center
          </fl-link>
          .
        </fl-text>
        <fl-text
          *ngSwitchCase="ErrorCodeApi.USER_ACCOUNT_LIMITED"
          i18n="Limited account error on milestone release"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
        >
          Your account has been limited for account security reasons. You can
          continue to work and receive payments, but you won't be able to
          transfer or withdraw funds. To verify your identity, visit the
          <fl-link
            flTrackingLabel="VerificationCenterLink"
            [link]="'/users/kyc/verificationCenter.php'"
            [newTab]="true"
          >
            Verification Center
          </fl-link>
          .
        </fl-text>
        <fl-text
          *ngSwitchCase="ErrorCodeApi.USER_ACCOUNT_FULL_RESTRICTION"
          i18n="Full restriction error on milestone release"
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
        >
          A restriction on your account has been assigned as a penalty. You can
          continue your project but you won't be able to transfer or release
          funds.
        </fl-text>
        <fl-text
          *ngSwitchDefault
          [color]="FontColor.ERROR"
          [textAlign]="TextAlign.CENTER"
          i18n="General error message for releasing milestones"
        >
          An unknown error occurred while trying to release the milestone.
          Please try again or contact support@freelancer.com
          <ng-container *ngIf="(submissionPromise | async)?.requestId">
            with request_id: {{ (submissionPromise | async)?.requestId }}.
          </ng-container>
        </fl-text>
      </fl-bit>

      <fl-button
        flTrackingLabel="MilestoneReleaseModalReleaseButton"
        i18n="Release milestone button"
        [busy]="submissionPromise && (submissionPromise | async) === null"
        [color]="ButtonColor.SECONDARY"
        [disabled]="!(canRelease$ | async)"
        [flMarginBottom]="Margin.XSMALL"
        [size]="ButtonSize.SMALL"
        (click)="releasePayment()"
      >
        Release Payment
      </fl-button>
      <fl-link
        flTrackingLabel="MilestoneReleaseModalCancelLink"
        i18n="Close release milestone modal"
        [color]="LinkColor.DARK"
        [hoverColor]="LinkHoverColor.DEFAULT"
        (click)="closeModal()"
      >
        Cancel
      </fl-link>
    </fl-bit>
  `,
  styleUrls: ['./milestone-release-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilestoneReleaseModalComponent implements OnInit {
  HeadingType = HeadingType;
  TextSize = TextSize;
  FontColor = FontColor;
  FontType = FontType;
  Margin = Margin;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  LinkHoverColor = LinkHoverColor;
  LinkColor = LinkColor;
  TextAlign = TextAlign;
  ErrorCodeApi = ErrorCodeApi;

  @Input() bid: Bid;
  @Input() escrowComInteractionRequired: boolean;
  @Input() isBidAwardedHourly: boolean;
  @Input() milestoneDoc: DatastoreDocument<'milestones'>;
  @Input() milestone: Milestone;
  @Input() totalPaidMilestonesAmount: number;

  checkControl = new FormControl(false, requiredTruthy(''));
  canRelease$: Rx.Observable<boolean>;

  errorCode: string | undefined;
  requestId: string | undefined;
  submissionPromise: Promise<BackendUpdateResponse<'milestones'>>;

  constructor(private modalRef: ModalRef<MilestoneReleaseModalComponent>) {}

  ngOnInit() {
    this.canRelease$ = this.checkControl.statusChanges.pipe(
      startWith(this.checkControl.status),
      map(status => status === 'VALID'),
    );
  }

  releasePayment() {
    this.submissionPromise = this.milestoneDoc
      .update(this.milestone)
      .then(response => {
        if (response.status === 'success') {
          // Milestone total amount >= bid amount, COMPLETE redirect to review tab
          if (
            !this.isBidAwardedHourly &&
            !this.escrowComInteractionRequired &&
            this.totalPaidMilestonesAmount + (this.milestone.amount || 0) >=
              this.bid.amount
          ) {
            this.closeModal({ type: 'COMPLETE', bidderId: this.bid.bidderId });
            return response;
          }
          this.closeModal({ type: 'RELEASE' });
          return response;
        }
        return response;
      });
  }

  // undefined for close without releasing
  // 'Complete' for release and project complete
  // 'Release' for just releasing without side effects
  closeModal(param?: ReleaseMilestoneRedirectSchema) {
    this.modalRef.close(param);
  }
}
